import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from 'lucide-react';

/**
 * NativeSelect — the platform `<select>`, styled to match the shadcn family.
 *
 * Use the Radix-based `Select` by default; it looks the same everywhere and
 * supports rich item content. Reach for this one when the dropdown must escape
 * the page's stacking context: Radix portals its listbox to `document.body` with
 * `z-50`, so inside a high-z-index overlay (the Sidekick panel sits at 9001) the
 * options render *behind* the panel and clicking the trigger appears to do
 * nothing. A native select's popup is drawn by the OS, outside the page's
 * stacking contexts entirely, so there is no z-index to lose — and no
 * `z-index: 9002` arms race with the next overlay.
 *
 * Also the better choice for a dense control (a header, a toolbar row) and on
 * mobile, where the OS picker beats a custom listbox.
 *
 * Trade-offs, so this stays a deliberate pick: options are plain text only (no
 * icons or two-line rows), and the popup itself cannot be styled — `<option>`
 * colours come from the OS, which is why the wrapper sets an explicit
 * background/colour on options rather than inheriting the popover tokens.
 */
const NativeSelect = React.forwardRef<HTMLSelectElement, React.ComponentProps<'select'>>(
  function NativeSelect({ className, children, ...props }, ref) {
    return (
      <div className="relative inline-flex items-center" data-slot="native-select-wrapper">
        <select
          ref={ref}
          data-slot="native-select"
          className={cn(
            // `appearance-none` + our own chevron, so the control matches
            // SelectTrigger instead of the OS's own arrow.
            'appearance-none h-9 w-full rounded-md border border-input bg-transparent py-2 pl-3 pr-8 text-sm text-foreground shadow-sm outline-none transition-[color,box-shadow]',
            'focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
            // The popup is OS-drawn and does not inherit the page's theme, so
            // options need explicit colours or they can render unreadable
            // (white-on-white in dark mode).
            '[&>option]:bg-popover [&>option]:text-popover-foreground',
            className
          )}
          {...props}
        >
          {children}
        </select>
        {/* Presentational: the select underneath owns all interaction. */}
        <ChevronDownIcon
          aria-hidden
          className="pointer-events-none absolute right-2.5 h-4 w-4 shrink-0 opacity-50 text-foreground"
        />
      </div>
    );
  }
);

export { NativeSelect };
